import { useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { useGetOrg } from "@src/api/organizations/get-organization";
import {
  mapRecContentActions,
  useGetRecommendations,
} from "@src/api/recommendations/get-recommendations";
import { DashboardPageName } from "@src/api/types/dashboards";
import {
  getRecommendationCopyHash,
  getRecommendationDismissKey,
} from "@src/components/recommendation/recommendation-dismissal";
import { RecommendationCard } from "@src/components/recommendation/RecommendationCard";
import { useDispatch, useSelector } from "@src/store";
import {
  dismissRecommendationCopy,
  getIsRecommendationCopyDismissed,
} from "@src/store/slices/appSettings";
import {
  mpRecommendationActionClicked,
  mpRecommendationDismissed,
  mpRecommendationViewed,
} from "@src/utils/mixpanel";

interface AnalyticRecommendationProps {
  pageName: DashboardPageName;
}

export const AnalyticRecommendation = ({
  pageName,
}: AnalyticRecommendationProps) => {
  const organization = useGetOrg();
  const orgId = organization.data?.org.id;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const viewedRef = useRef(false);

  const analyticsRecommendationQuery = useGetRecommendations({
    enabled: pageName === "overview",
  });

  const recommendation =
    analyticsRecommendationQuery.data?.recommendation?.resultsPage;

  const provenImpact =
    analyticsRecommendationQuery.data?.recommendation?.provenImpact;

  const recType =
    analyticsRecommendationQuery.data?.recommendation?.recType ?? null;

  const rawActions = useMemo(
    () =>
      recommendation
        ? mapRecContentActions(recommendation.actions, navigate)
        : [],
    [recommendation, navigate]
  );

  const actions = useMemo(
    () =>
      rawActions.map((action) => ({
        ...action,
        onClick: () => {
          mpRecommendationActionClicked({
            rec_type: recType,
            entry_point: "analytics_overview",
            action_label: action.label,
            action_destination: action.destination,
          });
          action.onClick();
        },
      })),
    [rawActions, recType]
  );

  const dismissKey = recommendation
    ? getRecommendationDismissKey("analytics", "results-rec")
    : undefined;
  const copyHash = recommendation
    ? getRecommendationCopyHash([
        recommendation.label,
        recommendation.text,
        ...actions.map((a) => a.label),
      ])
    : undefined;

  const isDismissed = useSelector(
    getIsRecommendationCopyDismissed(orgId, dismissKey, copyHash)
  );

  useEffect(() => {
    if (!viewedRef.current && recommendation && !isDismissed) {
      viewedRef.current = true;
      mpRecommendationViewed({
        rec_type: recType,
        entry_point: "analytics_overview",
        variant: "card",
      });
    }
  }, [recommendation, isDismissed, recType]);

  if (pageName !== "overview" || isDismissed || !recommendation) {
    return null;
  }

  return (
    <RecommendationCard
      actionsPosition="top"
      label={recommendation.label}
      headline={recommendation.text}
      insight={provenImpact?.text}
      actions={actions}
      onDismiss={() => {
        mpRecommendationDismissed({
          rec_type: recType,
          entry_point: "analytics_overview",
        });
        if (orgId && dismissKey && copyHash) {
          dispatch(
            dismissRecommendationCopy({
              orgId,
              recommendationKey: dismissKey,
              copyHash,
            })
          );
        }
      }}
    />
  );
};
